import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MultiSelectWithDisabledOptions from '../../../../common/_custom/MultiSelectDropdown/MultiSelectWithDisabledOptions';
import { fetchDataByConfig } from '../../../../common/ManageDealDrawer/ManageDealDrawerService';
import { polusBaseUrl } from '../../../../utils/axios-interceptor';
import { USER_TYPES } from '../../../../store/investor/constants';
import { DESIGNATION } from '../../../../utils/constants/commonConstants';
import { truncateByCharacters } from '../../../../utils/utils';
import { editIconPurple } from '../../../../assets/hostedassets';
import styles from './LenderUserPillCell.module.scss';

export type Option = {
    label: string;
    value: string;
    type?: string;
};

export interface LenderUser {
    name: string;
    userId: string;
    ownerType: typeof USER_TYPES.LENDER | typeof USER_TYPES.LENDER_TEAM;
}

export interface LenderUserPillCellProps {
    users: LenderUser[];
    rowId: string;
    isShared: number;
    onUsersUpdate: (rowId: string, selectedUserIds: string[], options: Option[]) => void;
    parentSelectedUsers?: Array<{ name: string; userId: string }>;
}

const LenderUserPillCell: React.FC<LenderUserPillCellProps> = ({ users, rowId, isShared, onUsersUpdate, parentSelectedUsers = [] }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [allUserOptions, setAllUserOptions] = useState<Option[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Store selections locally - only notify parent when dropdown closes to prevent component recreation
    const pendingSelectionsRef = useRef<string[]>(selectedUserIds);

    // Initialize selected users from props and merge with parent selected users
    useEffect(() => {
        if (users) {
            const initialUserOptions = users.map((user) => ({
                label: user.name,
                value: user.userId,
                type: user.ownerType === USER_TYPES.LENDER ? DESIGNATION.ADMIN : DESIGNATION.TEAM_MEMBER,
            }));

            // Convert parent selected users to options (these are team members added from dropdown)
            const parentUserOptions = parentSelectedUsers.map((user) => ({
                label: user.name,
                value: user.userId,
                type: DESIGNATION.TEAM_MEMBER,
            }));

            // Merge options: initial users + parent selected users (avoiding duplicates by userId)
            const existingIds = new Set(initialUserOptions.map((opt) => opt.value));
            const uniqueParentOptions = parentUserOptions.filter((opt) => !existingIds.has(opt.value));
            setAllUserOptions([...initialUserOptions, ...uniqueParentOptions]);

            // Compute union of initial user IDs and parent selected user IDs
            const initialUserIds = users.map((user) => user.userId);
            const parentUserIds = parentSelectedUsers.map((user) => user.userId);
            const unionUserIds = [...new Set([...initialUserIds, ...parentUserIds])];
            setSelectedUserIds(unionUserIds);
        }
    }, [users, parentSelectedUsers]);

    // Fetch users from API when dropdown opens
    useEffect(() => {
        if (isDropdownOpen && rowId) {
            const fetchUsers = async () => {
                const payload = {
                    orgId: rowId,
                    accountType: USER_TYPES.INVESTOR_TYPE,
                };

                const response = await fetchDataByConfig(payload, 'AICA_GET_ORG_USERS', 'GET', polusBaseUrl);

                if (response) {
                    const res = response.data.responseBody.data || [];
                    const options = res.map((user: any) => ({
                        label: user.name,
                        value: user.userId,
                        type: user.userType === USER_TYPES.LENDER ? DESIGNATION.ADMIN : DESIGNATION.TEAM_MEMBER,
                    }));
                    setAllUserOptions(options);
                }
            };
            fetchUsers();
        }
    }, [isDropdownOpen, rowId]);

    // Calculate dropdown position
    const calculateDropdownPosition = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 5,
                left: rect.left,
            });
        }
    };

    useEffect(() => {
        if (isDropdownOpen) {
            calculateDropdownPosition();
        }
    }, [isDropdownOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isInsideContainer = containerRef.current?.contains(target) ?? false;
            const isInsideDropdown = dropdownRef.current?.contains(target) ?? false;
            const isInsideAnyDropdownPortal = (target as Element)?.closest?.('[data-dropdown-portal="true"]') !== null;

            if (isDropdownOpen && !isInsideContainer && !isInsideDropdown && !isInsideAnyDropdownPortal) {
                // Notify parent with pending selections when closing
                onUsersUpdate(rowId, pendingSelectionsRef.current, allUserOptions);
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            // Small delay to ensure portal is rendered
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 10);
            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isDropdownOpen, rowId, allUserOptions, onUsersUpdate]);

    // Disabled users are those with ownerType LENDER
    const disabledUserIds = useMemo(() => users.filter((user) => user.ownerType === USER_TYPES.LENDER).map((user) => user.userId), [users]);

    const handleToggleSelection = (newSelected: string[]) => {
        setSelectedUserIds(newSelected);
        pendingSelectionsRef.current = newSelected;
        // Don't call onUsersUpdate here - it causes parent re-render which recreates this component
    };

    const selectedUserObjects = allUserOptions.filter((u) => selectedUserIds.includes(u.value));
    const firstName = truncateByCharacters(selectedUserObjects[0]?.label || '', 20);
    const remainingCount = selectedUserObjects.length - 1;

    const isActive = isShared !== 1;
    const pillClass = isActive ? styles.PillActive : styles.PillInactive;
    const moreTextClass = isActive ? styles.Active : styles.Inactive;

    const handleContainerClick = (e: React.MouseEvent) => {
        if (!isActive) return;
        e.stopPropagation();

        if (!isDropdownOpen) {
            // Opening dropdown
            calculateDropdownPosition();
            pendingSelectionsRef.current = selectedUserIds;
        } else {
            // Closing dropdown - notify parent with pending selections
            onUsersUpdate(rowId, pendingSelectionsRef.current, allUserOptions);
        }

        setIsDropdownOpen((prev) => !prev);
    };

    const dropdownContent = isDropdownOpen && (
        <div
            ref={dropdownRef}
            className={styles.DropdownPortal}
            data-dropdown-portal="true"
            style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
            }}
        >
            <MultiSelectWithDisabledOptions
                options={allUserOptions}
                disabledOptions={disabledUserIds}
                selectedItems={selectedUserIds}
                placeholder=""
                toggleSelection={handleToggleSelection}
                showInput={false}
                showAddButton={false}
                dropdownStyle={{ width: '300px' }}
                persistDropdown={true}
            />
        </div>
    );

    const containerClass = isActive ? styles.ActiveContainer : '';

    return (
        <div ref={containerRef}>
            <div className={`${styles.LenderUserPillContainer} ${containerClass}`} onClick={handleContainerClick}>
                <div className={styles.PillWrapper}>
                    <span className={`${styles.UserPill} ${pillClass}`}>{firstName}</span>
                    {remainingCount > 0 && <span className={`${styles.MoreText} ${moreTextClass}`}> +{remainingCount} more</span>}
                    {isActive && <img src={editIconPurple} alt="Edit" className={styles.EditIcon} />}
                </div>
            </div>
            {dropdownContent && createPortal(dropdownContent, document.body)}
        </div>
    );
};

export default LenderUserPillCell;
